// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Document cell - displays filename with link to view original.
 * Version selection moved to pipeline cards; metadata in Document Manager.
 */
import { useState, type FC } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import {
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
} from '@/components/ui'

import type { NodeApiClient } from '@/app-shell/services'
import { getDocumentViewUrl, type DocumentListItem } from '@/subsystems/documents'

export interface DocumentCellProps {
  document: DocumentListItem
  orgId: string
  wsId: string
  apiClient: NodeApiClient
}

export const DocumentCell: FC<DocumentCellProps> = ({
  document,
  orgId,
  wsId,
  apiClient,
}) => {
  const [loading, setLoading] = useState(false)
  const filename = document.latestFilename ?? document.name ?? document.documentId

  const handleViewDocument = async () => {
    setLoading(true)
    try {
      const url = await getDocumentViewUrl(apiClient, orgId, wsId, document.documentId)
      window.open(url, '_blank')
    } catch (err) {
      console.error('[DocumentCell] Failed to get view URL:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => void handleViewDocument()}
              disabled={loading}
              className="p-1 -ml-1 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              <span className="sr-only">View original document</span>
            </button>
          </TooltipTrigger>
          <DarkTooltipContent side="top">View original</DarkTooltipContent>
        </Tooltip>
      </TooltipProvider>
      <span className="font-medium text-foreground">{filename}</span>
    </div>
  )
}
